import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import {
  GraduationCap,
  Plus,
  Trash2,
  Save,
  ArrowRight,
  Loader2,
  Clock,
  BookOpen,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Shuffle,
  Key,
  ShieldCheck,
  AlertTriangle,
  Eye,
  EyeOff,
  Calendar,
  Lock,
  Edit,
  Upload,
  FileSpreadsheet,
  Search,
} from 'lucide-react';
import { toast } from 'sonner';
import ExamImporter from '../components/ExamImporter';
import RichTextEditor, { renderSafeHtml, stripHtml } from '../components/RichTextEditor';

const CreateExam = ({ editMode = false }) => {
  const navigate = useNavigate();
  const { examId } = useParams();
  const { token, isTeacher } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingExam, setLoadingExam] = useState(editMode);
  const [expandedQuestion, setExpandedQuestion] = useState(0);
  const [canEditQuestions, setCanEditQuestions] = useState(true);
  const [hasActiveSessions, setHasActiveSessions] = useState(false);
  const [showImportPreview, setShowImportPreview] = useState(false);
  const [importedQuestions, setImportedQuestions] = useState([]);
  const fileInputRef = useRef(null);
  // Question-bank import state
  const [bankDialogOpen, setBankDialogOpen] = useState(false);
  const [bankTopics, setBankTopics] = useState([]);
  const [bankLoadingTopics, setBankLoadingTopics] = useState(false);
  const [bankSelectedTopic, setBankSelectedTopic] = useState(null);
  const [bankTopicQuestions, setBankTopicQuestions] = useState([]);
  const [bankLoadingQuestions, setBankLoadingQuestions] = useState(false);
  const [bankSelectedIds, setBankSelectedIds] = useState({});
  const [importerOpen, setImporterOpen] = useState(false);
  const [questionSearch, setQuestionSearch] = useState('');
  const [bankQuestionSearch, setBankQuestionSearch] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  const [examData, setExamData] = useState({
    title: '',
    description: '',
    duration_minutes: 30,
    grade: '',
    section: '',
    is_active: true,
    use_smart_questions: false,
    smart_easy_count: 3,
    smart_medium_count: 4,
    smart_hard_count: 3,
    require_access_code: false,
    max_attempts: 1,
    max_violations: 2,
    // Result visibility settings - Enhanced
    show_score_to_student: true,
    show_percentage: true,
    show_pass_fail_only: false,
    show_correct_answers: false,
    show_wrong_answers: false,
    show_only_student_answers: true,
    show_violation_details: false,
    show_time_spent: true,
    show_rank_among_students: false,
    result_custom_message: '',
    passing_percentage: 50,
    // Result release settings
    result_release_mode: 'immediate',
    result_release_datetime: null,
    results_released: true,
    // Auto-close setting
    auto_close_on_violations: true,
    allow_early_submit: true,
    questions: [
      {
        question_type: 'multiple_choice',
        text: '',
        options: [
          { id: crypto.randomUUID(), text: '' },
          { id: crypto.randomUUID(), text: '' },
          { id: crypto.randomUUID(), text: '' },
          { id: crypto.randomUUID(), text: '' },
        ],
        correct_answer: '',
        points: 1,
        difficulty: 'medium',
      },
    ],
  });

  const grades = [
    'الصف الأول',
    'الصف الثاني',
    'الصف الثالث',
    'الصف الرابع',
    'الصف الخامس',
    'الصف السادس',
    'الصف السابع',
    'الصف الثامن',
    'الصف التاسع',
    'الصف العاشر',
    'الصف الحادي عشر',
    'الصف الثاني عشر',
    'غير ذلك',
  ];

  const sections = ['أ', 'ب', 'ج', 'د', 'هـ', 'و'];

  const questionTypes = [
    { value: 'multiple_choice', label: 'اختيار من متعدد' },
    { value: 'true_false', label: 'صح أو خطأ' },
    { value: 'short_answer', label: 'إجابة قصيرة' },
    { value: 'paragraph', label: 'مقالي' },
  ];

  const difficultyLevels = [
    { value: 'easy', label: 'سهل', color: 'bg-green-100 text-green-800' },
    { value: 'medium', label: 'متوسط', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'hard', label: 'صعب', color: 'bg-red-100 text-red-800' },
  ];

  // Load exam data for edit mode
  useEffect(() => {
    if (editMode && examId) {
      loadExamForEdit();
    }
  }, [editMode, examId]);

  const loadExamForEdit = async () => {
    setLoadingExam(true);
    try {
      const response = await fetch(`${API_URL}/api/exams/${examId}/edit`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setCanEditQuestions(data.can_edit_questions);
        setHasActiveSessions(data.has_active_sessions);
        
        // Set exam data with all new fields
        setExamData({
          title: data.title || '',
          description: data.description || '',
          duration_minutes: data.duration_minutes || 30,
          grade: data.grade || '',
          section: data.section || '',
          is_active: data.is_active ?? true,
          use_smart_questions: data.use_smart_questions || false,
          smart_easy_count: data.smart_easy_count || 3,
          smart_medium_count: data.smart_medium_count || 4,
          smart_hard_count: data.smart_hard_count || 3,
          require_access_code: data.require_access_code || false,
          max_attempts: data.max_attempts || 1,
          max_violations: data.max_violations || 2,
          // Enhanced visibility settings
          show_score_to_student: data.show_score_to_student ?? true,
          show_percentage: data.show_percentage ?? true,
          show_pass_fail_only: data.show_pass_fail_only || false,
          show_correct_answers: data.show_correct_answers || false,
          show_wrong_answers: data.show_wrong_answers || false,
          show_only_student_answers: data.show_only_student_answers ?? true,
          show_violation_details: data.show_violation_details || false,
          show_time_spent: data.show_time_spent ?? true,
          show_rank_among_students: data.show_rank_among_students || false,
          result_custom_message: data.result_custom_message || '',
          passing_percentage: data.passing_percentage || 50,
          // Release settings
          result_release_mode: data.result_release_mode || 'immediate',
          result_release_datetime: data.result_release_datetime || null,
          results_released: data.results_released ?? true,
          auto_close_on_violations: data.auto_close_on_violations ?? true,
          allow_early_submit: data.allow_early_submit ?? true,
          questions: data.questions || [],
        });
      } else if (response.status === 403) {
        toast.error('غير مصرح لك بتعديل هذا الاختبار');
        navigate('/teacher/dashboard');
      } else {
        toast.error('فشل تحميل بيانات الاختبار');
        navigate('/teacher/dashboard');
      }
    } catch (error) {
      console.error('Error loading exam:', error);
      toast.error('حدث خطأ أثناء تحميل الاختبار');
      navigate('/teacher/dashboard');
    }
    setLoadingExam(false);
  };

  const handleExamChange = (field, value) => {
    setExamData({ ...examData, [field]: value });
  };

  const handleQuestionChange = (index, field, value) => {
    const updatedQuestions = [...examData.questions];
    updatedQuestions[index] = { ...updatedQuestions[index], [field]: value };
    
    // If changing question type, reset options and correct_answer
    if (field === 'question_type') {
      if (value === 'true_false') {
        updatedQuestions[index].options = [
          { id: 'true', text: 'صح' },
          { id: 'false', text: 'خطأ' },
        ];
        updatedQuestions[index].correct_answer = '';
      } else if (value === 'multiple_choice') {
        updatedQuestions[index].options = [
          { id: crypto.randomUUID(), text: '' },
          { id: crypto.randomUUID(), text: '' },
          { id: crypto.randomUUID(), text: '' },
          { id: crypto.randomUUID(), text: '' },
        ];
        updatedQuestions[index].correct_answer = '';
      } else {
        updatedQuestions[index].options = null;
        updatedQuestions[index].correct_answer = '';
      }
    }
    
    setExamData({ ...examData, questions: updatedQuestions });
  };

  const handleOptionChange = (questionIndex, optionIndex, value) => {
    const updatedQuestions = [...examData.questions];
    updatedQuestions[questionIndex].options[optionIndex].text = value;
    setExamData({ ...examData, questions: updatedQuestions });
  };

  const addQuestion = () => {
    const newQuestion = {
      question_type: 'multiple_choice',
      text: '',
      options: [
        { id: crypto.randomUUID(), text: '' },
        { id: crypto.randomUUID(), text: '' },
        { id: crypto.randomUUID(), text: '' },
        { id: crypto.randomUUID(), text: '' },
      ],
      correct_answer: '',
      points: 1,
      difficulty: 'medium',
    };
    setExamData({
      ...examData,
      questions: [...examData.questions, newQuestion],
    });
    setExpandedQuestion(examData.questions.length);
  };

  const removeQuestion = (index) => {
    if (examData.questions.length === 1) {
      toast.error('يجب أن يحتوي الاختبار على سؤال واحد على الأقل');
      return;
    }
    const updatedQuestions = examData.questions.filter((_, i) => i !== index);
    setExamData({ ...examData, questions: updatedQuestions });
    if (expandedQuestion >= updatedQuestions.length) {
      setExpandedQuestion(updatedQuestions.length - 1);
    }
  };

  // Import questions from Google Forms CSV
  const handleImportCSV = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result;
        const lines = text.split('\n').map(line => {
          // Parse CSV line handling quoted fields
          const result = [];
          let current = '';
          let inQuotes = false;
          
          for (let i = 0; i < line.length; i++) {
            const char = line[i];
            if (char === '"') {
              inQuotes = !inQuotes;
            } else if (char === ',' && !inQuotes) {
              result.push(current.trim());
              current = '';
            } else {
              current += char;
            }
          }
          result.push(current.trim());
          return result;
        });
        
        // Find header row and question columns
        const header = lines[0] || [];
        const questions = [];
        
        // Try to detect Google Forms format
        // Typical format: Timestamp, Email, Question1, Question2, ...
        // Or direct questions with options
        
        for (let i = 1; i < lines.length; i++) {
          const row = lines[i];
          if (!row || row.length === 0 || !row[0]) continue;
          
          // Check if this looks like a question row
          for (let j = 0; j < row.length; j++) {
            const cell = row[j];
            if (cell && cell.length > 10 && cell.includes('؟') || cell.includes('?')) {
              // This looks like a question
              const questionText = cell;
              
              // Try to find options in subsequent cells or rows
              const options = [];
              const possibleOptions = ['أ', 'ب', 'ج', 'د', 'A', 'B', 'C', 'D'];
              
              // Check next few cells for options
              for (let k = j + 1; k < Math.min(row.length, j + 5); k++) {
                if (row[k] && row[k].length > 0 && row[k].length < 200) {
                  options.push({
                    id: crypto.randomUUID(),
                    text: row[k]
                  });
                }
              }
              
              if (options.length >= 2) {
                questions.push({
                  question_type: 'multiple_choice',
                  text: questionText,
                  options: options.length >= 4 ? options.slice(0, 4) : [
                    ...options,
                    ...Array(4 - options.length).fill(null).map(() => ({ id: crypto.randomUUID(), text: '' }))
                  ],
                  correct_answer: '',
                  points: 1,
                  difficulty: 'medium'
                });
              }
            }
          }
        }
        
        // Alternative: Simple format - each row is a question
        if (questions.length === 0) {
          for (let i = 1; i < Math.min(lines.length, 50); i++) {
            const row = lines[i];
            if (!row || row.length < 2) continue;
            
            // First cell is question, next 4 are options, last is correct answer
            const questionText = row[0];
            if (!questionText || questionText.length < 5) continue;
            
            const options = [];
            for (let j = 1; j <= 4 && j < row.length; j++) {
              if (row[j]) {
                options.push({
                  id: crypto.randomUUID(),
                  text: row[j]
                });
              }
            }
            
            // Pad options if needed
            while (options.length < 4) {
              options.push({ id: crypto.randomUUID(), text: '' });
            }
            
            // Try to detect correct answer
            let correctAnswer = '';
            if (row.length > 5 && row[5]) {
              correctAnswer = row[5];
            }
            
            questions.push({
              question_type: 'multiple_choice',
              text: questionText,
              options,
              correct_answer: correctAnswer,
              points: 1,
              difficulty: 'medium'
            });
          }
        }
        
        if (questions.length === 0) {
          toast.error('لم يتم العثور على أسئلة في الملف. تأكد من تنسيق CSV صحيح.');
          return;
        }
        
        setImportedQuestions(questions);
        setShowImportPreview(true);
        toast.success(`تم العثور على ${questions.length} سؤال`);
        
      } catch (error) {
        console.error('CSV parsing error:', error);
        toast.error('فشل قراءة الملف. تأكد من أنه ملف CSV صحيح.');
      }
    };
    
    reader.readAsText(file, 'UTF-8');
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // ===== Question Bank Import =====
  const openBankImport = async () => {
    setBankDialogOpen(true);
    setBankSelectedTopic(null);
    setBankTopicQuestions([]);
    setBankSelectedIds({});
    setBankLoadingTopics(true);
    try {
      const r = await fetch(`${API_URL}/api/topics`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (r.ok) {
        const data = await r.json();
        setBankTopics(Array.isArray(data) ? data : []);
      } else {
        toast.error('تعذّر تحميل بنك الأسئلة');
      }
    } catch {
      toast.error('حدث خطأ أثناء تحميل بنك الأسئلة');
    }
    setBankLoadingTopics(false);
  };

  const selectBankTopic = async (topic) => {
    setBankSelectedTopic(topic);
    setBankSelectedIds({});
    setBankTopicQuestions([]);
    setBankLoadingQuestions(true);
    try {
      const r = await fetch(`${API_URL}/api/topics/${topic.id}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (r.ok) {
        const data = await r.json();
        setBankTopicQuestions(data.questions || []);
      } else {
        toast.error('تعذّر تحميل الأسئلة');
      }
    } catch {
      toast.error('حدث خطأ أثناء تحميل الأسئلة');
    }
    setBankLoadingQuestions(false);
  };

  const toggleBankQuestion = (qid) => {
    setBankSelectedIds(prev => ({ ...prev, [qid]: !prev[qid] }));
  };

  const toggleSelectAllBank = () => {
    if (bankTopicQuestions.every(q => bankSelectedIds[q.id])) {
      setBankSelectedIds({});
    } else {
      const map = {};
      bankTopicQuestions.forEach(q => { map[q.id] = true; });
      setBankSelectedIds(map);
    }
  };

  const confirmBankImport = () => {
    const chosen = bankTopicQuestions.filter(q => bankSelectedIds[q.id]);
    if (chosen.length === 0) {
      toast.error('اختر سؤالاً واحداً على الأقل');
      return;
    }
    // Copy questions with fresh option ids so they are independent from the bank.
    // We must remap correct_answer from the OLD option id to the NEW one — otherwise
    // the correct answer link breaks when ids are regenerated.
    const copied = chosen.map(q => {
      const originalOptions = q.options || [];
      const newOptions = originalOptions.map(o => ({
        id: crypto.randomUUID(),
        text: o.text,
      }));
      let newCorrect = q.correct_answer || '';
      if (q.question_type === 'multiple_choice') {
        const oldIdx = originalOptions.findIndex(o => o.id === q.correct_answer);
        if (oldIdx >= 0 && newOptions[oldIdx]) {
          newCorrect = newOptions[oldIdx].id;
        } else {
          // Fallback: try text match (handles older banks that stored text in correct_answer)
          const byText = newOptions.find((o, i) => originalOptions[i]?.text === q.correct_answer);
          if (byText) newCorrect = byText.id;
        }
      }
      return {
        question_type: q.question_type,
        text: q.text,
        options: newOptions,
        correct_answer: newCorrect,
        points: q.points ?? 1,
        difficulty: q.difficulty || 'medium',
      };
    });
    setExamData(prev => ({ ...prev, questions: [...prev.questions, ...copied] }));
    setBankDialogOpen(false);
    setBankSelectedTopic(null);
    setBankTopicQuestions([]);
    setBankSelectedIds({});
    toast.success(`تم إضافة ${copied.length} سؤال من بنك الأسئلة`);
  };

  const confirmImportQuestions = () => {
    setExamData({
      ...examData,
      questions: [...examData.questions, ...importedQuestions]
    });
    setShowImportPreview(false);
    setImportedQuestions([]);
    toast.success(`تم إضافة ${importedQuestions.length} سؤال`);
  };

  // ── Difficulty counts of the questions actually in this exam ────────────────
  const difficultyCounts = examData.questions.reduce((acc, q) => {
    const d = q.difficulty || 'medium';
    acc[d] = (acc[d] || 0) + 1;
    return acc;
  }, { easy: 0, medium: 0, hard: 0 });

  // For smart exams: do we have enough questions of each difficulty in the
  // pool to satisfy the requested counts?
  const smartShortages = examData.use_smart_questions
    ? [
        { key: 'easy',   label: 'سهلة',   need: examData.smart_easy_count   || 0, have: difficultyCounts.easy   },
        { key: 'medium', label: 'متوسطة', need: examData.smart_medium_count || 0, have: difficultyCounts.medium },
        { key: 'hard',   label: 'صعبة',   need: examData.smart_hard_count   || 0, have: difficultyCounts.hard   },
      ].filter(x => x.need > x.have)
    : [];

  const validateExam = () => {
    if (!examData.title.trim()) {
      toast.error('يرجى إدخال عنوان الاختبار');
      return false;
    }
    if (!examData.grade) {
      toast.error('يرجى اختيار الصف');
      return false;
    }
    if (examData.duration_minutes < 1) {
      toast.error('يجب أن تكون مدة الاختبار دقيقة واحدة على الأقل');
      return false;
    }
    if (examData.result_release_mode === 'scheduled' && !examData.result_release_datetime) {
      toast.error('يرجى تحديد تاريخ ووقت إصدار النتائج');
      return false;
    }

    // Smart-exam difficulty validation: ensure the bank has enough questions
    // of each difficulty to satisfy the requested counts.
    if (examData.use_smart_questions && smartShortages.length > 0) {
      const lines = smartShortages.map(s =>
        `• ${s.label}: المطلوب ${s.need} — المتوفر ${s.have} (ناقص ${s.need - s.have})`
      ).join('\n');
      toast.error(`بنك الأسئلة غير كافٍ للتوزيع المطلوب:\n${lines}`, { duration: 7000 });
      return false;
    }

    for (let i = 0; i < examData.questions.length; i++) {
      const q = examData.questions[i];
      const plainText = stripHtml(q.text || '');
      if (!plainText) {
        toast.error(`يرجى إدخال نص السؤال ${i + 1}`);
        setExpandedQuestion(i);
        return false;
      }

      if (q.question_type === 'multiple_choice') {
        const filledOptions = q.options?.filter(o => stripHtml(o.text || '')) || [];
        if (filledOptions.length < 2) {
          toast.error(`يجب إدخال خيارين على الأقل للسؤال ${i + 1}`);
          setExpandedQuestion(i);
          return false;
        }
        if (!q.correct_answer) {
          toast.error(`يرجى تحديد الإجابة الصحيحة للسؤال ${i + 1}`);
          setExpandedQuestion(i);
          return false;
        }
      }

      if (q.question_type === 'true_false' && !q.correct_answer) {
        toast.error(`يرجى تحديد الإجابة الصحيحة للسؤال ${i + 1}`);
        setExpandedQuestion(i);
        return false;
      }
    }

    return true;
  };

  // Question search filter (applied to the exam's own question list)
  const normalizedQuestionSearch = questionSearch.trim().toLowerCase();
  const questionMatchesSearch = (q) => {
    if (!normalizedQuestionSearch) return true;
    const haystack = [
      stripHtml(q.text || ''),
      ...((q.options || []).map(o => stripHtml(o.text || ''))),
    ].join(' ').toLowerCase();
    return haystack.includes(normalizedQuestionSearch);
  };

  // Filter for bank-import dialog
  const normalizedBankSearch = bankQuestionSearch.trim().toLowerCase();
  const bankQuestionMatchesSearch = (q) => {
    if (!normalizedBankSearch) return true;
    const haystack = [
      stripHtml(q.text || ''),
      ...((q.options || []).map(o => stripHtml(o.text || ''))),
    ].join(' ').toLowerCase();
    return haystack.includes(normalizedBankSearch);
  };
  const filteredBankQuestions = bankTopicQuestions.filter(bankQuestionMatchesSearch);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateExam()) return;

    setLoading(true);
    try {
      // Clean up questions before sending
      const cleanedQuestions = examData.questions.map(q => {
        const cleaned = {
          id: q.id || null, // Preserve existing question ID for edits
          question_type: q.question_type,
          text: q.text,
          points: q.points,
          correct_answer: q.correct_answer || null,
          difficulty: q.difficulty || 'medium',
        };
        
        if (q.question_type === 'multiple_choice' || q.question_type === 'true_false') {
          cleaned.options = q.options?.filter(o => o.text.trim()).map(o => ({
            id: o.id,
            text: o.text,
          }));
        }
        
        return cleaned;
      });

      const url = editMode 
        ? `${API_URL}/api/exams/${examId}`
        : `${API_URL}/api/exams`;
      
      const method = editMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...examData,
          section: examData.section === 'all' ? '' : examData.section,
          questions: cleanedQuestions,
        }),
      });

      if (response.ok) {
        toast.success(editMode ? 'تم تحديث الاختبار بنجاح' : 'تم إنشاء الاختبار بنجاح');
        navigate('/teacher/dashboard');
      } else if (response.status === 403) {
        toast.error('غير مصرح لك بتعديل هذا الاختبار');
      } else {
        const data = await response.json();
        toast.error(data.detail || (editMode ? 'فشل تحديث الاختبار' : 'فشل إنشاء الاختبار'));
      }
    } catch (error) {
      toast.error(editMode ? 'حدث خطأ أثناء تحديث الاختبار' : 'حدث خطأ أثناء إنشاء الاختبار');
    }
    setLoading(false);
  };

  const getQuestionTypeBadgeClass = (type) => {
    switch (type) {
      case 'multiple_choice':
        return 'bg-blue-500/20 text-blue-400';
      case 'true_false':
        return 'bg-purple-500/20 text-purple-400';
      case 'short_answer':
        return 'bg-green-100 text-green-800';
      case 'paragraph':
        return 'bg-[#EF9F27]/15 text-[#EF9F27]';
      default:
        return 'bg-[#FFFFFF]/50 text-[#412402]';
    }
  };

  if (!isTeacher) {
    navigate('/teacher/login');
    return null;
  }

  if (loadingExam) {
    return (
      <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#EF9F27] mx-auto mb-4" />
          <p className="text-[#412402]/60">جاري تحميل بيانات الاختبار...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFFFF]">
      {/* Header */}
      <header className="bg-[#FFFFFF]/80 border-b border-[#E8DFCF] sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/teacher/dashboard" className="flex items-center gap-3">
              <img src="/logo.png" alt="Logo" className="w-10 h-10 rounded-lg object-cover" />
              <span className="text-xl font-bold text-[#412402]"> المعتمد في اللغة العربية</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/teacher/dashboard')}
            className="text-[#412402]/60 hover:text-[#412402] mb-4"
          >
            <ArrowRight className="w-4 h-4 ml-2" />
            العودة للوحة التحكم
          </Button>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-[#412402]">
              {editMode ? 'تعديل الاختبار' : 'إنشاء اختبار جديد'}
            </h1>
            {editMode && (
              <Badge variant="outline" className="text-blue-400 border-blue-500/30">
                <Edit className="w-3 h-3 ml-1" />
                وضع التعديل
              </Badge>
            )}
          </div>
          {editMode && !canEditQuestions && (
            <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800 text-sm">
              <AlertTriangle className="w-4 h-4 inline ml-1" />
              تنبيه: هناك طلاب أكملوا هذا الاختبار. تعديل الأسئلة لن يؤثر على المحاولات السابقة ولكن سيؤثر على المحاولات الجديدة.
            </div>
          )}
          {editMode && hasActiveSessions && (
            <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-red-800 text-sm">
              <AlertTriangle className="w-4 h-4 inline ml-1" />
              تنبيه: هناك طلاب يؤدون الاختبار حالياً.
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Exam Details Card */}
          <Card className="border-[#E8DFCF]">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#412402]">معلومات الاختبار</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">عنوان الاختبار *</Label>
                <Input
                  id="title"
                  placeholder="مثال: اختبار النحو والصرف - الوحدة الأولى"
                  value={examData.title}
                  onChange={(e) => handleExamChange('title', e.target.value)}
                  data-testid="exam-title-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">وصف الاختبار (اختياري)</Label>
                <Textarea
                  id="description"
                  placeholder="ادخل وصفاً مختصراً للاختبار..."
                  value={examData.description}
                  onChange={(e) => handleExamChange('description', e.target.value)}
                  rows={3}
                  data-testid="exam-description-input"
                />
              </div>


              <div className="grid sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>الصف *</Label>
                  <Select
                    value={examData.grade}
                    onValueChange={(value) => handleExamChange('grade', value)}
                  >
                    <SelectTrigger data-testid="exam-grade-select">
                      <BookOpen className="w-4 h-4 ml-2 text-[#412402]/40" />
                      <SelectValue placeholder="اختر الصف" />
                    </SelectTrigger>
                    <SelectContent>
                      {grades.map((grade) => (
                        <SelectItem key={grade} value={grade}>
                          {grade}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>الشعبة (اختياري)</Label>
                  <Select
                    value={examData.section || "all"}
                    onValueChange={(value) => handleExamChange('section', value === "all" ? "" : value)}
                  >
                    <SelectTrigger data-testid="exam-section-select">
                      <SelectValue placeholder="جميع الشعب" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">جميع الشعب</SelectItem>
                      {sections.map((section) => (
                        <SelectItem key={section} value={section}>
                          شعبة {section}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="duration">مدة الاختبار (بالدقائق) *</Label>
                  <div className="relative">
                    <Clock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#412402]/40" />
                    <Input
                      id="duration"
                      type="number"
                      min="1"
                      max="180"
                      value={examData.duration_minutes}
                      onChange={(e) => handleExamChange('duration_minutes', parseInt(e.target.value) || 1)}
                      className="pr-10"
                      data-testid="exam-duration-input"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Smart Question Bank */}
          <Card className="bg-[#FFFFFF]/80 border border-[#E8DFCF] border-dashed shadow-md">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#412402] flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#EF9F27]" />
                بنك الأسئلة الذكي
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="use_smart_questions"
                  checked={examData.use_smart_questions}
                  onCheckedChange={(checked) => handleExamChange('use_smart_questions', checked)}
                  data-testid="smart-questions-checkbox"
                />
                <Label htmlFor="use_smart_questions" className="cursor-pointer text-sm">
                  تفعيل الاختيار العشوائي للأسئلة حسب الصعوبة
                </Label>
              </div>
              
              {examData.use_smart_questions && (
                <div className="bg-[#FFFFFF]/80 rounded-lg p-4 border border-[#E8DFCF]">
                  <div className="flex items-center gap-2 mb-4 text-sm text-[#412402]/60">
                    <Shuffle className="w-4 h-4" />
                    <span>سيتم اختيار أسئلة عشوائية لكل طالب حسب التوزيع التالي:</span>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-green-700">عدد الأسئلة السهلة</Label>
                      <Input
                        type="number"
                        min="0"
                        max="50"
                        value={examData.smart_easy_count}
                        onChange={(e) => handleExamChange('smart_easy_count', parseInt(e.target.value) || 0)}
                        className="border-green-200 focus:border-green-400"
                        data-testid="smart-easy-count-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-yellow-700">عدد الأسئلة المتوسطة</Label>
                      <Input
                        type="number"
                        min="0"
                        max="50"
                        value={examData.smart_medium_count}
                        onChange={(e) => handleExamChange('smart_medium_count', parseInt(e.target.value) || 0)}
                        className="border-yellow-200 focus:border-yellow-400"
                        data-testid="smart-medium-count-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-red-700">عدد الأسئلة الصعبة</Label>
                      <Input
                        type="number"
                        min="0"
                        max="50"
                        value={examData.smart_hard_count}
                        onChange={(e) => handleExamChange('smart_hard_count', parseInt(e.target.value) || 0)}
                        className="border-red-200 focus:border-red-400"
                        data-testid="smart-hard-count-input"
                      />
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-[#FFFFFF] rounded-lg">
                    <p className="text-xs text-[#412402]/60">
                      <strong>إجمالي الأسئلة المطلوبة:</strong> {examData.smart_easy_count + examData.smart_medium_count + examData.smart_hard_count} سؤال
                      <br />
                      <strong>المتوفر حالياً في الأسئلة أدناه:</strong>
                      {` سهلة: ${difficultyCounts.easy} • متوسطة: ${difficultyCounts.medium} • صعبة: ${difficultyCounts.hard}`}
                    </p>
                    {smartShortages.length > 0 && (
                      <div className="mt-3 p-3 rounded-lg border border-red-300 bg-red-50 text-sm text-red-800" data-testid="difficulty-shortage-banner">
                        <div className="flex items-center gap-2 font-semibold mb-1">
                          <AlertTriangle className="w-4 h-4" />
                          عدد الأسئلة لا يكفي لتلبية التوزيع المطلوب
                        </div>
                        <ul className="list-disc pr-5 space-y-0.5">
                          {smartShortages.map(s => (
                            <li key={s.key}>
                              {`${s.label}: المطلوب ${s.need} — المتوفر ${s.have} (ناقص ${s.need - s.have})`}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Security Settings */}
          <Card className="bg-[#FFFFFF]/80 border border-blue-500/30 border-dashed shadow-[0_0_15px_rgba(59,130,246,0.1)]">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#412402] flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-blue-400" />
                إعدادات الأمان
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Access Code */}
              <div className="flex items-center gap-3">
                <Checkbox
                  id="require_access_code"
                  checked={examData.require_access_code}
                  onCheckedChange={(checked) => handleExamChange('require_access_code', checked)}
                  data-testid="require-access-code-checkbox"
                />
                <Label htmlFor="require_access_code" className="cursor-pointer text-sm flex items-center gap-2">
                  <Key className="w-4 h-4 text-blue-400" />
                  يتطلب رمز دخول لبدء الاختبار
                </Label>
              </div>
              {examData.require_access_code && (
                <div className="bg-blue-500/10 rounded-lg p-3 text-sm text-blue-400">
                  سيتم إنشاء رمز دخول مؤقت من لوحة تحكم المعلم قبل بدء الاختبار
                </div>
              )}

              {/* Max Attempts */}
              <div className="grid sm:grid-cols-2 gap-4 pt-2">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-[#EF9F27]" />
                    الحد الأقصى للمحاولات
                  </Label>
                  <Input
                    type="number"
                    min="1"
                    max="10"
                    value={examData.max_attempts}
                    onChange={(e) => handleExamChange('max_attempts', parseInt(e.target.value) || 1)}
                    className="border-[#E8DFCF]"
                    data-testid="max-attempts-input"
                  />
                  <p className="text-xs text-[#412402]/50">عدد المرات المسموح للطالب لدخول الاختبار</p>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-red-600" />
                    الحد الأقصى للمخالفات
                  </Label>
                  <Input
                    type="number"
                    min="1"
                    max="10"
                    value={examData.max_violations}
                    onChange={(e) => handleExamChange('max_violations', parseInt(e.target.value) || 2)}
                    className="border-[#E8DFCF]"
                    data-testid="max-violations-input"
                  />
                  <p className="text-xs text-[#412402]/50">عدد المخالفات قبل إنهاء الاختبار تلقائياً</p>
                </div>
              </div>

              {/* Auto-close toggle */}
              <div className="flex items-center gap-3 pt-2 border-t border-blue-500/30">
                <Checkbox
                  id="auto_close_on_violations"
                  checked={examData.auto_close_on_violations}
                  onCheckedChange={(checked) => handleExamChange('auto_close_on_violations', checked)}
                  data-testid="auto-close-checkbox"
                />
                <Label htmlFor="auto_close_on_violations" className="cursor-pointer text-sm">
                  إنهاء الاختبار تلقائياً عند تجاوز الحد الأقصى للمخالفات
                </Label>
              </div>
              {!examData.auto_close_on_violations && (
                <div className="bg-[#EF9F27]/15/50 rounded-lg p-3 text-sm text-[#EF9F27]">
                  ⚠️ سيتم تسجيل المخالفات فقط دون إنهاء الاختبار. يمكنك إيقاف الطالب يدوياً من شاشة المراقبة المباشرة.
                </div>
              )}

              {/* Early submit toggle */}
              <div className="flex items-center gap-3 pt-2 border-t border-blue-500/30">
                <Checkbox
                  id="allow_early_submit"
                  checked={examData.allow_early_submit}
                  onCheckedChange={(checked) => handleExamChange('allow_early_submit', checked)}
                  data-testid="allow-early-submit-checkbox"
                />
                <Label htmlFor="allow_early_submit" className="cursor-pointer text-sm">
                  السماح للطالب بالتسليم قبل انتهاء الوقت
                </Label>
              </div>
              {!examData.allow_early_submit && (
                <div className="bg-[#EF9F27]/15/50 rounded-lg p-3 text-sm text-[#EF9F27]">
                  ⚠️ لن يتمكن الطالب من التسليم بنفسه، وسيتم تسليم إجاباته تلقائياً عند انتهاء وقته. وقت كل طالب يبدأ من لحظة دخوله، لذا إذا دخل الطلاب في أوقات مختلفة استخدم الإصدار اليدوي أو المجدول للنتائج حتى لا يراها أحد قبل انتهاء الجميع.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Result Visibility Settings */}
          <Card className="bg-[#FFFFFF]/80 border border-purple-500/30 border-dashed shadow-[0_0_15px_rgba(168,85,247,0.1)]">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#412402] flex items-center gap-2">
                <Eye className="w-5 h-5 text-purple-400" />
                إعدادات عرض النتائج
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Result Release Mode */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">وقت إصدار النتائج</Label>
                <RadioGroup
                  value={examData.result_release_mode}
                  onValueChange={(value) => handleExamChange('result_release_mode', value)}
                  className="space-y-2"
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="immediate" id="release-immediate" data-testid="release-immediate-radio" />
                    <Label htmlFor="release-immediate" className="cursor-pointer text-sm">فوري بعد انتهاء الاختبار</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="manual" id="release-manual" data-testid="release-manual-radio" />
                    <Label htmlFor="release-manual" className="cursor-pointer text-sm">يدوي (إصدار من لوحة التحكم)</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="scheduled" id="release-scheduled" data-testid="release-scheduled-radio" />
                    <Label htmlFor="release-scheduled" className="cursor-pointer text-sm">مجدول في وقت محدد</Label>
                  </div>
                </RadioGroup>
              </div>

              {examData.result_release_mode === 'scheduled' && (
                <div className="bg-[#FFFFFF]/80 rounded-lg p-4 border border-purple-500/30 space-y-2">
                  <Label className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-purple-400" />
                    تاريخ ووقت إصدار النتائج (بتوقيت الأردن)
                  </Label>
                  <Input
                    type="datetime-local"
                    value={examData.result_release_datetime || ''}
                    onChange={(e) => handleExamChange('result_release_datetime', e.target.value)}
                    className="border-purple-500/30"
                    data-testid="release-datetime-input"
                  />
                </div>
              )}

              {examData.result_release_mode === 'manual' && (
                <div className="bg-purple-500/10 rounded-lg p-3 text-sm text-purple-400">
                  سيتم إصدار النتائج يدوياً من لوحة تحكم المعلم بعد انتهاء جميع الطلاب من الاختبار
                </div>
              )}

              {/* Passing Percentage */}
              <div className="bg-[#FFFFFF]/80 rounded-lg p-4 border border-purple-500/30 space-y-2">
                <Label className="flex items-center gap-2 text-sm font-medium">
                  نسبة النجاح (%)
                </Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={examData.passing_percentage}
                  onChange={(e) => handleExamChange('passing_percentage', parseInt(e.target.value) || 50)}
                  className="w-32 border-purple-500/30"
                  data-testid="passing-percentage-input"
                />
              </div>

              {/* Visibility Options - Enhanced */}
              <div className="space-y-3 pt-4 border-t border-purple-500/30">
                <Label className="text-sm font-medium">ما يظهر للطالب في النتيجة</Label>
                
                {/* Pass/Fail Only Mode */}
                <div className="bg-[#EF9F27]/10 border border-[#E8DFCF] rounded-lg p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="show_pass_fail_only"
                      checked={examData.show_pass_fail_only}
                      onCheckedChange={(checked) => handleExamChange('show_pass_fail_only', checked)}
                      data-testid="show-pass-fail-only-checkbox"
                    />
                    <Label htmlFor="show_pass_fail_only" className="cursor-pointer text-sm font-medium text-[#EF9F27]">
                      عرض ناجح/راسب فقط (بدون درجة)
                    </Label>
                  </div>
                  {examData.show_pass_fail_only && (
                    <p className="text-xs text-[#EF9F27] mr-6">سيرى الطالب فقط "ناجح ✅" أو "راسب ❌" بدون أي درجات</p>
                  )}
                </div>
                
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="show_score"
                      checked={examData.show_score_to_student}
                      onCheckedChange={(checked) => handleExamChange('show_score_to_student', checked)}
                      disabled={examData.show_pass_fail_only}
                      data-testid="show-score-checkbox"
                    />
                    <Label htmlFor="show_score" className={`cursor-pointer text-sm ${examData.show_pass_fail_only ? 'text-[#412402]/40' : ''}`}>
                      عرض الدرجة الكلية
                    </Label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="show_percentage"
                      checked={examData.show_percentage}
                      onCheckedChange={(checked) => handleExamChange('show_percentage', checked)}
                      disabled={examData.show_pass_fail_only}
                      data-testid="show-percentage-checkbox"
                    />
                    <Label htmlFor="show_percentage" className={`cursor-pointer text-sm ${examData.show_pass_fail_only ? 'text-[#412402]/40' : ''}`}>
                      عرض النسبة المئوية
                    </Label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="show_rank_among_students"
                      checked={examData.show_rank_among_students}
                      onCheckedChange={(checked) => handleExamChange('show_rank_among_students', checked)}
                      data-testid="show-rank-checkbox"
                    />
                    <Label htmlFor="show_rank_among_students" className="cursor-pointer text-sm">
                      عرض ترتيب الطالب بين زملائه
                    </Label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="show_time_spent"
                      checked={examData.show_time_spent}
                      onCheckedChange={(checked) => handleExamChange('show_time_spent', checked)}
                      data-testid="show-time-checkbox"
                    />
                    <Label htmlFor="show_time_spent" className="cursor-pointer text-sm">
                      عرض وقت البدء والانتهاء
                    </Label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="show_violation_details"
                      checked={examData.show_violation_details}
                      onCheckedChange={(checked) => handleExamChange('show_violation_details', checked)}
                      data-testid="show-violations-checkbox"
                    />
                    <Label htmlFor="show_violation_details" className="cursor-pointer text-sm">
                      عرض تفاصيل المخالفات
                    </Label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="show_only_student_answers"
                      checked={examData.show_only_student_answers}
                      onCheckedChange={(checked) => handleExamChange('show_only_student_answers', checked)}
                      data-testid="show-student-answers-checkbox"
                    />
                    <Label htmlFor="show_only_student_answers" className="cursor-pointer text-sm">عرض إجابات الطالب</Label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="show_correct"
                      checked={examData.show_correct_answers}
                      onCheckedChange={(checked) => handleExamChange('show_correct_answers', checked)}
                      data-testid="show-correct-checkbox"
                    />
                    <Label htmlFor="show_correct" className="cursor-pointer text-sm">عرض الإجابات الصحيحة</Label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="show_wrong"
                      checked={examData.show_wrong_answers}
                      onCheckedChange={(checked) => handleExamChange('show_wrong_answers', checked)}
                      data-testid="show-wrong-checkbox"
                    />
                    <Label htmlFor="show_wrong" className="cursor-pointer text-sm">تمييز الإجابات الخاطئة</Label>
                  </div>
                </div>
              </div>

              {/* Custom Message */}
              <div className="space-y-2 pt-4 border-t border-purple-500/30">
                <Label className="text-sm font-medium">رسالة مخصصة تظهر مع النتيجة (اختياري)</Label>
                <Textarea
                  placeholder="مثال: أحسنت! استمر في العمل الجاد..."
                  value={examData.result_custom_message}
                  onChange={(e) => handleExamChange('result_custom_message', e.target.value)}
                  rows={2}
                  className="border-purple-500/30"
                  data-testid="custom-message-textarea"
                />
              </div>
            </CardContent>
          </Card>

          {/* Questions */}
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h2 className="text-lg font-semibold text-[#412402]">الأسئلة ({examData.questions.length})</h2>
              <div className="relative">
                <Search className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[#412402]/40 pointer-events-none" />
                <Input
                  value={questionSearch}
                  onChange={(e) => setQuestionSearch(e.target.value)}
                  placeholder="ابحث في الأسئلة…"
                  className="pr-8 w-56"
                  dir="auto"
                  data-testid="exam-question-search-input"
                />
              </div>
            </div>

            {examData.questions.map((question, qIndex) => questionMatchesSearch(question) && (
              <Card key={qIndex} className="border-[#E8DFCF]">
                <CardHeader 
                  className="cursor-pointer hover:bg-[#FFFFFF] transition-colors"
                  onClick={() => setExpandedQuestion(expandedQuestion === qIndex ? -1 : qIndex)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 bg-[#EF9F27]/15 text-[#EF9F27] rounded-lg flex items-center justify-center font-semibold">
                        {qIndex + 1}
                      </span>
                      <div>
                        <p
                          dir="auto"
                          className="font-medium text-[#412402]"
                          dangerouslySetInnerHTML={renderSafeHtml(stripHtml(question.text) || 'سؤال جديد')}
                        />
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getQuestionTypeBadgeClass(question.question_type)}`}>
                            {questionTypes.find(t => t.value === question.question_type)?.label}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${difficultyLevels.find(d => d.value === (question.difficulty || 'medium'))?.color}`}>
                            {difficultyLevels.find(d => d.value === (question.difficulty || 'medium'))?.label}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeQuestion(qIndex);
                        }}
                        data-testid={`remove-question-btn-${qIndex}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      {expandedQuestion === qIndex ? (
                        <ChevronUp className="w-5 h-5 text-[#412402]/40" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-[#412402]/40" />
                      )}
                    </div>
                  </div>
                </CardHeader>

                {expandedQuestion === qIndex && (
                  <CardContent className="border-t border-[#E8DFCF] space-y-4">
                    <div className="grid sm:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>نوع السؤال</Label>
                        <Select
                          value={question.question_type}
                          onValueChange={(value) => handleQuestionChange(qIndex, 'question_type', value)}
                        >
                          <SelectTrigger data-testid={`question-type-select-${qIndex}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {questionTypes.map((type) => (
                              <SelectItem key={type.value} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>مستوى الصعوبة</Label>
                        <Select
                          value={question.difficulty || 'medium'}
                          onValueChange={(value) => handleQuestionChange(qIndex, 'difficulty', value)}
                        >
                          <SelectTrigger data-testid={`question-difficulty-select-${qIndex}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {difficultyLevels.map((level) => (
                              <SelectItem key={level.value} value={level.value}>
                                <span className={`px-2 py-0.5 rounded text-xs ${level.color}`}>
                                  {level.label}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>الدرجة</Label>
                        <Input
                          type="number"
                          min="1"
                          value={question.points}
                          onChange={(e) => handleQuestionChange(qIndex, 'points', parseInt(e.target.value) || 1)}
                          data-testid={`question-points-input-${qIndex}`}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>نص السؤال *</Label>
                      <RichTextEditor
                        value={question.text}
                        onChange={(v) => handleQuestionChange(qIndex, 'text', v)}
                        rows={3}
                        placeholder="ادخل نص السؤال هنا… (يدعم التنسيق: عريض / مائل / تحته خط)"
                        testid={`question-text-input-${qIndex}`}
                      />
                    </div>

                    {/* Multiple Choice Options */}
                    {question.question_type === 'multiple_choice' && (
                      <div className="space-y-3">
                        <Label>الخيارات *</Label>
                        <RadioGroup
                          value={question.correct_answer}
                          onValueChange={(value) => handleQuestionChange(qIndex, 'correct_answer', value)}
                        >
                          {question.options?.map((option, oIndex) => (
                            <div key={option.id} className="flex items-start gap-3">
                              <RadioGroupItem
                                value={option.id}
                                id={`q${qIndex}-o${oIndex}`}
                                className="mt-2"
                                data-testid={`question-${qIndex}-option-${oIndex}-radio`}
                              />
                              <div className="flex-1">
                                <RichTextEditor
                                  value={option.text}
                                  onChange={(v) => handleOptionChange(qIndex, oIndex, v)}
                                  rows={1}
                                  placeholder={`الخيار ${oIndex + 1}`}
                                  testid={`question-${qIndex}-option-${oIndex}-input`}
                                />
                              </div>
                            </div>
                          ))}
                        </RadioGroup>
                        <p className="text-xs text-[#412402]/50">اختر الإجابة الصحيحة بالنقر على الدائرة</p>
                      </div>
                    )}

                    {/* True/False Options */}
                    {question.question_type === 'true_false' && (
                      <div className="space-y-3">
                        <Label>الإجابة الصحيحة *</Label>
                        <RadioGroup
                          value={question.correct_answer}
                          onValueChange={(value) => handleQuestionChange(qIndex, 'correct_answer', value)}
                          className="flex gap-4"
                        >
                          <div className="flex items-center gap-2">
                            <RadioGroupItem 
                              value="true" 
                              id={`q${qIndex}-true`}
                              data-testid={`question-${qIndex}-true-radio`}
                            />
                            <Label htmlFor={`q${qIndex}-true`} className="cursor-pointer">صح</Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <RadioGroupItem 
                              value="false" 
                              id={`q${qIndex}-false`}
                              data-testid={`question-${qIndex}-false-radio`}
                            />
                            <Label htmlFor={`q${qIndex}-false`} className="cursor-pointer">خطأ</Label>
                          </div>
                        </RadioGroup>
                      </div>
                    )}

                    {/* Short Answer */}
                    {question.question_type === 'short_answer' && (
                      <div className="space-y-2">
                        <Label>الإجابة الصحيحة (للتصحيح التلقائي)</Label>
                        <Input
                          placeholder="ادخل الإجابة الصحيحة..."
                          value={question.correct_answer || ''}
                          onChange={(e) => handleQuestionChange(qIndex, 'correct_answer', e.target.value)}
                          data-testid={`question-${qIndex}-correct-answer-input`}
                          disabled={false}
                        />
                      </div>
                    )}

                    {/* Paragraph - No correct answer needed */}
                    {question.question_type === 'paragraph' && (
                      <p className="text-sm text-[#412402]/50 bg-[#FFFFFF] p-3 rounded-lg">
                        الأسئلة المقالية تتطلب تصحيحاً يدوياً من المعلم
                      </p>
                    )}
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
          <div className="flex gap-3 flex-wrap">
            <Button
                type="button"
                variant="outline"
                onClick={addQuestion}
                data-testid="add-question-btn"
              >
                <Plus className="w-4 h-4 ml-2" />
                إضافة سؤال
            </Button>
            
            {/* Import from CSV */}
            <input
              type="file"
              accept=".csv"
              ref={fileInputRef}
              onChange={handleImportCSV}
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
              data-testid="import-csv-btn"
            >
              <Upload className="w-4 h-4 ml-2" />
              استيراد من CSV
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setImporterOpen(true)}
              className="border-emerald-500/40 text-emerald-700 hover:bg-emerald-500/10"
              data-testid="import-word-gform-btn"
            >
              <Upload className="w-4 h-4 ml-2" />
              استيراد من Word / Google Forms
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={openBankImport}
              className="border-amber-500/40 text-amber-700 hover:bg-amber-500/10"
              data-testid="import-from-bank-btn"
            >
              <BookOpen className="w-4 h-4 ml-2" />
              استيراد من بنك الأسئلة
            </Button>
          </div>

          <ExamImporter
            open={importerOpen}
            onOpenChange={setImporterOpen}
            token={token}
            onImport={(qs) => {
              const cleaned = qs.map(q => ({
                question_type: q.question_type,
                text: q.text,
                options: q.options || undefined,
                correct_answer: q.correct_answer || '',
                points: q.points || 1,
                difficulty: q.difficulty || 'medium',
              }));
              setExamData(prev => ({ ...prev, questions: [...prev.questions, ...cleaned] }));
              toast.success(`تم إضافة ${cleaned.length} سؤال إلى الاختبار`);
            }}
          />

          {/* Question Bank Import Dialog */}
          {bankDialogOpen && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
              <Card className="w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
                <CardHeader className="border-b">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">استيراد من بنك الأسئلة</CardTitle>
                    {bankSelectedTopic && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setBankSelectedTopic(null); setBankTopicQuestions([]); setBankSelectedIds({}); }}
                      >
                        رجوع للقائمة
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="overflow-y-auto flex-1 py-3">
                  {!bankSelectedTopic ? (
                    bankLoadingTopics ? (
                      <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-amber-700" /></div>
                    ) : bankTopics.length === 0 ? (
                      <div className="py-10 text-center text-slate-500 text-sm">لا توجد مواضيع في بنك الأسئلة بعد</div>
                    ) : (
                      <div className="divide-y">
                        {bankTopics.map(t => (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => selectBankTopic(t)}
                            className="w-full text-right py-3 px-2 hover:bg-amber-50 flex items-center justify-between"
                          >
                            <div>
                              <div className="font-semibold text-[#412402]">{t.title}</div>
                              <div className="text-xs text-slate-500">{t.grade} {t.section ? `- ${t.section}` : ''} • {(t.questions?.length || 0)} سؤال</div>
                            </div>
                            <ChevronDown className="w-4 h-4 -rotate-90" />
                          </button>
                        ))}
                      </div>
                    )
                  ) : (
                    bankLoadingQuestions ? (
                      <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-amber-700" /></div>
                    ) : bankTopicQuestions.length === 0 ? (
                      <div className="py-10 text-center text-slate-500 text-sm">لا توجد أسئلة في هذا الموضوع</div>
                    ) : (
                      <div>
                        <div className="flex items-center justify-between mb-3 sticky top-0 bg-white pb-2 gap-2 flex-wrap">
                          <div className="text-sm text-[#412402]/70">
                            {Object.values(bankSelectedIds).filter(Boolean).length} / {filteredBankQuestions.length} مختار
                          </div>
                          <div className="relative flex-1 min-w-[180px] max-w-xs">
                            <Search className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[#412402]/40 pointer-events-none" />
                            <Input
                              value={bankQuestionSearch}
                              onChange={(e) => setBankQuestionSearch(e.target.value)}
                              placeholder="ابحث…"
                              className="pr-8"
                              dir="auto"
                              data-testid="bank-question-search-input"
                            />
                          </div>
                          <Button type="button" size="sm" variant="outline" onClick={toggleSelectAllBank}>
                            {filteredBankQuestions.length > 0 && filteredBankQuestions.every(q => bankSelectedIds[q.id]) ? 'إلغاء تحديد الكل' : 'تحديد الكل'}
                          </Button>
                        </div>
                        <div className="space-y-2">
                          {filteredBankQuestions.length === 0 ? (
                            <div className="py-6 text-center text-sm text-slate-500">لا توجد نتائج مطابقة</div>
                          ) : filteredBankQuestions.map((q, idx) => (
                            <label
                              key={q.id}
                              className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer border ${bankSelectedIds[q.id] ? 'bg-amber-50 border-amber-300' : 'bg-white border-slate-200 hover:bg-slate-50'}`}
                            >
                              <Checkbox
                                checked={!!bankSelectedIds[q.id]}
                                onCheckedChange={() => toggleBankQuestion(q.id)}
                                className="mt-1"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                  <span className="text-xs text-slate-500">#{idx + 1}</span>
                                  <Badge variant="outline" className="text-xs">{q.difficulty || 'medium'}</Badge>
                                  <Badge variant="outline" className="text-xs">{q.points || 1} نقطة</Badge>
                                </div>
                                <div dir="auto" className="text-sm font-medium text-[#412402] whitespace-pre-wrap" dangerouslySetInnerHTML={renderSafeHtml(q.text)} />
                                {(q.options && q.options.length > 0) && (
                                  <div className="grid grid-cols-2 gap-1 mt-2">
                                    {q.options.map((o) => (
                                      <div
                                        key={o.id}
                                        dir="auto"
                                        className={`text-xs p-1 rounded ${q.correct_answer === o.id ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-700'}`}
                                        dangerouslySetInnerHTML={renderSafeHtml(o.text)}
                                      />
                                    ))}
                                  </div>
                                )}
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    )
                  )}
                </CardContent>
                <div className="p-4 border-t flex gap-3 justify-end">
                  <Button variant="outline" onClick={() => setBankDialogOpen(false)}>إلغاء</Button>
                  {bankSelectedTopic && (
                    <Button
                      onClick={confirmBankImport}
                      className="bg-amber-700 hover:bg-amber-800"
                      disabled={Object.values(bankSelectedIds).filter(Boolean).length === 0}
                    >
                      إضافة المختار
                    </Button>
                  )}
                </div>
              </Card>
            </div>
          )}
          
          {/* Import Preview Modal */}
          {showImportPreview && (
            <div className="fixed inset-0 bg-[#FFFFFF]/50 flex items-center justify-center z-50 p-4">
              <Card className="w-full max-w-2xl max-h-[80vh] overflow-hidden">
                <CardHeader>
                  <CardTitle className="text-lg">معاينة الأسئلة المستوردة ({importedQuestions.length})</CardTitle>
                </CardHeader>
                <CardContent className="overflow-y-auto max-h-[50vh]">
                  <div className="space-y-3">
                    {importedQuestions.map((q, idx) => (
                      <div key={idx} className="p-3 bg-[#FFFFFF] rounded-lg">
                        <p dir="auto" className="font-medium text-sm mb-2 whitespace-pre-wrap" dangerouslySetInnerHTML={renderSafeHtml(`${idx + 1}. ${q.text || ''}`)} />
                        <div className="grid grid-cols-2 gap-2">
                          {q.options.filter(o => o.text).map((opt, oidx) => (
                            <div
                              key={opt.id}
                              dir="auto"
                              className={`text-xs p-2 rounded ${q.correct_answer === opt.id ? 'bg-green-100 text-green-700' : 'bg-[#FFFFFF]/80'}`}
                              dangerouslySetInnerHTML={renderSafeHtml(`${String.fromCharCode(1571 + oidx)}. ${opt.text}`)}
                            />
                          ))}
                        </div>
                        {!q.correct_answer && (
                          <p className="text-xs text-[#EF9F27] mt-2">⚠️ لم يتم تحديد الإجابة الصحيحة</p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
                <div className="p-4 border-t flex gap-3 justify-end">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowImportPreview(false);
                      setImportedQuestions([]);
                    }}
                  >
                    إلغاء
                  </Button>
                  <Button 
                    onClick={confirmImportQuestions}
                    className="bg-[#EF9F27] hover:bg-[#D88E1F]"
                  >
                    إضافة {importedQuestions.length} سؤال
                  </Button>
                </div>
              </Card>
            </div>
          )}
          
          {/* Submit Button */}
          <div className="flex items-center justify-end gap-4 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/teacher/dashboard')}
            >
              إلغاء
            </Button>
            <Button
              type="submit"
              className="bg-[#EF9F27] hover:bg-[#D88E1F]"
              disabled={loading}
              data-testid="submit-exam-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 ml-2 animate-spin" />
                  جاري الحفظ...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 ml-2" />
                  {editMode ? 'تحديث الاختبار' : 'حفظ الاختبار'}
                </>
              )}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default CreateExam;
